import React, { useState } from 'react';
import type { ImageGalleryComponentProps } from './types';
import { ButtonLink } from '../shared/ButtonLink';
import { useThemeSafe } from '../../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../../contexts/CustomColorsContext';
import { getFontFamily } from '../../utils/fontLoader';
import { getThemeStyles } from '../../styles/themes';
import { useImageGalleryColors, getBackgroundStyle } from './useImageGalleryColors';
import { useLightbox } from './useLightbox';
import { useLazyLoading } from './useLazyLoading';
import { sortItems, filterItemsByCategory, getUniqueCategories } from './utils';
import { renderTextWithLineBreaks } from '../../utils/textFormatting';
import { GridLayout } from './GridLayout';
import { MasonryLayout } from './MasonryLayout';
import { CarouselLayout } from './CarouselLayout';
import { DividerLayout } from './DividerLayout';
import { SlideshowLayout } from './SlideshowLayout';
import { Lightbox } from './Lightbox';
import { FilterButtons } from './FilterButtons';

export const ImageGalleryComponent: React.FC<ImageGalleryComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const [activeFilter, setActiveFilter] = useState<string>('All');

  // Derived data
  const sortedItems = sortItems(data.items || []);
  const filteredItems = filterItemsByCategory(sortedItems, activeFilter, data.filtering);
  const availableCategories = getUniqueCategories(sortedItems);

  // Hooks
  const colors = useImageGalleryColors(data, themeStyles);
  const imageRefs = useLazyLoading(data.lazyLoading, filteredItems);
  const {
    lightboxOpen,
    lightboxIndex,
    openLightbox,
    closeLightbox,
    handlePrevImage,
    handleNextImage,
  } = useLightbox(filteredItems, data.lightbox);

  const backgroundStyle = getBackgroundStyle(colors.backgroundColor);

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.imageGallery?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.imageGallery?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    if (!data.buttons || data.buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        {data.buttons.map((button, index) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  const layoutProps = {
    filteredItems,
    colors,
    data,
    imageRefs,
    onImageClick: openLightbox,
  };

  return (
    <section className="py-12 md:py-16 lg:py-24" style={backgroundStyle}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title and Subtitle */}
        {(data.showTitle || data.showSubtitle) && (
          <div className="text-center mb-8 md:mb-12">
            {data.showTitle && data.title && (
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: colors.titleColor, ...headingFontStyle }}
                dangerouslySetInnerHTML={{
                  __html: renderTextWithLineBreaks(data.title),
                }}
              />
            )}
            {data.showSubtitle && data.subtitle && (
              <p
                className="text-lg md:text-xl"
                style={{ color: colors.subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{
                  __html: renderTextWithLineBreaks(data.subtitle),
                }}
              />
            )}
          </div>
        )}

        {/* Filter Buttons */}
        {data.filtering && (
          <FilterButtons
            categories={availableCategories}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            textColor={colors.textColor}
            filterButtonColor={colors.filterButtonColor}
            filterButtonActiveColor={colors.filterButtonActiveColor}
          />
        )}

        {/* Gallery Layout */}
        {data.layout === 'masonry' && <MasonryLayout {...layoutProps} />}
        {data.layout === 'carousel' && <CarouselLayout {...layoutProps} />}
        {data.layout === 'grid' && <GridLayout {...layoutProps} />}
        {data.layout === 'divider' && <DividerLayout {...layoutProps} />}
        {data.layout === 'slideshow' && <SlideshowLayout {...layoutProps} />}

        {/* Call-to-Action Buttons */}
        {renderButtons()}

        {/* Lightbox */}
        <Lightbox
          isOpen={lightboxOpen}
          currentIndex={lightboxIndex}
          items={filteredItems}
          onClose={closeLightbox}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      </div>
    </section>
  );
};

export default ImageGalleryComponent;
